import {useEffect, useRef} from 'react'
import {t} from '@github-ui/swp-core/lib/utils/localization'
import {Image, MinimalVideoPlayer, Text} from '@primer/react-brand/esm'
import {getMediaSizeVars} from '../../_utils/media-size-vars'
import {DESKTOP_MEDIA_QUERY, type SlideMedia} from './hero-slide-media'

type Props = {
  media: SlideMedia
  title?: string
  descriptionId: string
  isSelected?: boolean
}

export function HeroSlideMedia({media, title, descriptionId, isSelected = true}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)

  // The player pauses while its slide is hidden and resumes where it left off; restart from the beginning instead.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Syncs the video element, which is outside React, with the selected tab.
    if (isSelected && videoRef.current) videoRef.current.currentTime = 0
  }, [isSelected])

  const {fallback, desktop} = media
  const file = fallback.fields.asset.fields.file
  if (!file) return null

  const description = fallback.fields.description ?? fallback.fields.asset.fields.description
  const imageDimensions = file.details?.image
  const width = fallback.fields.width ?? imageDimensions?.width
  const height = fallback.fields.height ?? imageDimensions?.height
  const desktopUrl = desktop?.fields.asset.fields.file?.url
  // Unitless sizes for CSS (aspect-ratio), since the <picture>/<video> only carry the default asset's size.
  const sizeVars = getMediaSizeVars(fallback, desktop)

  if (imageDimensions) {
    return desktopUrl ? (
      <Image
        as="picture"
        src={file.url}
        sources={[{srcset: desktopUrl, media: DESKTOP_MEDIA_QUERY}]}
        width={width}
        height={height}
        alt={description ?? ''}
        style={sizeVars}
      />
    ) : (
      <Image src={file.url} width={width} height={height} alt={description ?? ''} />
    )
  }

  const describedBy = description ? descriptionId : undefined

  return (
    <>
      {description ? (
        <Text className="sr-only" id={descriptionId}>
          {description}
        </Text>
      ) : null}
      {/* `key`: the browser only picks a <source> when it loads, so remount if the assets change. */}
      <MinimalVideoPlayer
        ref={videoRef}
        key={`${file.url}|${desktopUrl ?? ''}`}
        width={fallback.fields.width}
        height={fallback.fields.height}
        title={title ?? t('Video')}
        aria-describedby={describedBy}
        style={sizeVars}
      >
        {desktopUrl ? <source src={desktopUrl} media={DESKTOP_MEDIA_QUERY} /> : null}
        <source src={file.url} />
      </MinimalVideoPlayer>
    </>
  )
}
